import { type Company, uuidSchema } from '@jaf/contracts';

/**
 * Escopo escolhido no cabeçalho: o grupo inteiro (todas as empresas que o usuário pode ver),
 * uma matriz com as filiais dela, uma única empresa ou uma seleção personalizada de empresas.
 */
export type CompanyScope =
  | { readonly kind: 'GROUP' }
  | { readonly kind: 'HEADQUARTERS'; readonly headquartersId: string }
  | { readonly kind: 'COMPANY'; readonly companyId: string }
  | { readonly kind: 'CUSTOM'; readonly companyIds: readonly string[] };

/** Escopo na query dos relatórios (`scope` e, na seleção personalizada, `companyIds`). */
export interface ReportScopeQuery {
  scope: string;
  companyIds?: string[];
}

/** Escopo padrão: o grupo inteiro. */
export const GROUP_SCOPE: CompanyScope = { kind: 'GROUP' };

/** Escopo validado contra as empresas permitidas e as empresas que ele filtra. */
export interface ResolvedCompanyScope {
  scope: CompanyScope;
  /** `null` = sem restrição (grupo); senão, as empresas do escopo. */
  companyIds: string[] | null;
}

/** Opção do seletor de escopo. */
export interface ScopeOption {
  key: string;
  label: string;
  scope: CompanyScope;
}

const KIND_LABELS: Readonly<Record<Company['kind'], string>> = {
  HEADQUARTERS: 'Matriz',
  BRANCH: 'Filial',
};

const displayName = (company: Company): string => company.tradeName ?? company.legalName;

function branchesOf(headquartersId: string, companies: readonly Company[]): Company[] {
  return companies.filter(
    (company) => company.kind === 'BRANCH' && company.headquartersId === headquartersId,
  );
}

/**
 * Valida o escopo contra as empresas permitidas (as que a API devolve ao usuário) e calcula as
 * empresas que ele filtra. Escopo que deixou de ser permitido volta para o grupo.
 */
export function resolveCompanyScope(
  scope: CompanyScope,
  companies: readonly Company[],
): ResolvedCompanyScope {
  const byId = new Map(companies.map((company) => [company.id, company]));
  if (scope.kind === 'COMPANY' && byId.has(scope.companyId)) {
    return { scope, companyIds: [scope.companyId] };
  }
  if (scope.kind === 'CUSTOM') {
    const chosen = new Set(scope.companyIds);
    const companyIds = companies
      .filter((company) => chosen.has(company.id))
      .map((company) => company.id);
    if (companyIds.length > 0) return { scope: { kind: 'CUSTOM', companyIds }, companyIds };
  }
  if (scope.kind === 'HEADQUARTERS' && byId.get(scope.headquartersId)?.kind === 'HEADQUARTERS') {
    const branches = branchesOf(scope.headquartersId, companies).map((company) => company.id);
    return { scope, companyIds: [scope.headquartersId, ...branches] };
  }
  return { scope: GROUP_SCOPE, companyIds: null };
}

/** Escopo na query dos relatórios: a API resolve as empresas e recorta pelas permissões. */
export function reportScopeQuery(scope: CompanyScope): ReportScopeQuery {
  if (scope.kind === 'CUSTOM') return { scope: 'custom', companyIds: [...scope.companyIds] };
  return { scope: scopeKey(scope) };
}

/** Chave estável do escopo (valor do seletor e da preferência salva). */
export function scopeKey(scope: CompanyScope): string {
  if (scope.kind === 'COMPANY') return `company:${scope.companyId}`;
  if (scope.kind === 'HEADQUARTERS') return `headquarters:${scope.headquartersId}`;
  if (scope.kind === 'CUSTOM') return `custom:${scope.companyIds.join(',')}`;
  return 'group';
}

/** Converte a chave de volta no escopo; chave desconhecida ou malformada devolve `null`. */
export function scopeFromKey(key: string): CompanyScope | null {
  if (key === 'group') return GROUP_SCOPE;
  const [kind, id = ''] = key.split(':', 2);
  if (kind === 'custom') {
    const companyIds = id.split(',');
    return companyIds.every((companyId) => uuidSchema.safeParse(companyId).success)
      ? { kind: 'CUSTOM', companyIds }
      : null;
  }
  if (!uuidSchema.safeParse(id).success) return null;
  if (kind === 'company') return { kind: 'COMPANY', companyId: id };
  if (kind === 'headquarters') return { kind: 'HEADQUARTERS', headquartersId: id };
  return null;
}

function companyOption(company: Company): ScopeOption {
  const scope: CompanyScope = { kind: 'COMPANY', companyId: company.id };
  return {
    key: scopeKey(scope),
    label: `${displayName(company)} · ${KIND_LABELS[company.kind]}`,
    scope,
  };
}

/**
 * Opções do seletor: o grupo; para cada matriz, "matriz + filiais" (se houver filial visível),
 * a matriz e as filiais dela; por fim, filiais cuja matriz o usuário não vê.
 */
export function scopeOptions(companies: readonly Company[]): ScopeOption[] {
  const options: ScopeOption[] = [
    { key: scopeKey(GROUP_SCOPE), label: 'Grupo (todas as empresas)', scope: GROUP_SCOPE },
  ];
  const listed = new Set<string>();
  for (const headquarters of companies.filter((company) => company.kind === 'HEADQUARTERS')) {
    const branches = branchesOf(headquarters.id, companies);
    if (branches.length > 0) {
      const scope: CompanyScope = { kind: 'HEADQUARTERS', headquartersId: headquarters.id };
      options.push({
        key: scopeKey(scope),
        label: `${displayName(headquarters)} + filiais`,
        scope,
      });
    }
    for (const company of [headquarters, ...branches]) {
      options.push(companyOption(company));
      listed.add(company.id);
    }
  }
  for (const company of companies.filter((item) => !listed.has(item.id))) {
    options.push(companyOption(company));
  }
  return options;
}
